import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

// @ts-expect-error TS2724
import type { ProviderPriceListItemModel, ProviderPriceListItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['providerPriceLists', 'providerPriceListItems']

type ProviderPriceListItemFilters = {
  provider_price_list_id?: number,
  service_product_id?: number,
}

type UseProviderPriceListItemsOptions = {
  filters?: ProviderPriceListItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: ProviderPriceListItemRequestOptions,
}

function useProviderPriceListItems(options: UseProviderPriceListItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    provider_price_list_id: filterProviderPriceListId,
    service_product_id: filterServiceProductId,
  } = filters

  const {
    updatedEntities: {
      providerPriceLists: providerPriceListsUpdatedAt,
      providerPriceListItems: providerPriceListItemsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { providerPriceListItems } = useSelector(reduxState => reduxState.entities)

  const filteredProviderPriceListItems = useMemo(() => {
    const filtered = Object.values(providerPriceListItems)
      .filter((providerPriceListItem: ProviderPriceListItemModel) => {
        const {
          provider_price_list_id,
          service_product_id,
        } = providerPriceListItem

        const matchProviderPriceListId = matchFilterNumber(provider_price_list_id, filterProviderPriceListId)
        const matchServiceProductId = matchFilterNumber(service_product_id, filterServiceProductId)

        return matchProviderPriceListId && matchServiceProductId
      })

    return sortArrayBy(filtered, 'asc', 'quantity')
  }, [providerPriceListsUpdatedAt, providerPriceListItemsUpdatedAt, JSON.stringify(filters)])

  const filteredProviderPriceListItemsCount = filteredProviderPriceListItems.length
  const hasFilteredProviderPriceListItems = !!filteredProviderPriceListItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredProviderPriceListItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingProviderPriceListItems } = useReduxAction(
    'providerPriceListItems',
    'loadProviderPriceListItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredProviderPriceListItems,
    filteredProviderPriceListItemsCount,
    hasFilteredProviderPriceListItems,
    loading: loadingProviderPriceListItems,
    visiblePriceListItems: filteredProviderPriceListItems.filter(priceListItem => !priceListItem.hidden),
  }
}

export default useProviderPriceListItems
